import { useQuery } from '@tanstack/react-query';
import { getAcceptPaymentsSetupStatus, getPaymentAggregators, getSiteUrls } from './endpoints';
import { apiKeys } from './keys';
import { hasPremiumDomainConnected } from '../utils/domain';
import { isWixPaymentProviderSupported } from '../utils/payment';
import { useMsId } from '../stores/appStore';
import {
  GetPaymentAggregateResponse,
  GetMetaSiteUrlsResponse,
} from '@wix/ambassador-gg-store-setup-v1-store-setup-service/types';

interface PrimaryMetaSiteUrlInfo {
  hasPremiumDomain: boolean;
  url: string;
}

interface AcceptPaymentsSetupStatus {
  isAcceptPaymentsSetup: boolean;
}

function metaSiteUrlsQueryOptions(metaSiteId: string | null) {
  return {
    queryKey: apiKeys.queries.getSiteUrls(metaSiteId),
    queryFn: () => getSiteUrls(),
    staleTime: 5 * 60 * 1000,
    retry: 3,
    retryDelay: 2000,
    enabled: !!metaSiteId,
  };
}

export function usePaymentProviders() {
  return useQuery<GetPaymentAggregateResponse, Error, { providers: GetPaymentAggregateResponse; isAvailable: boolean }>(
    {
      queryKey: apiKeys.queries.getPaymentAggregators(),
      queryFn: () => getPaymentAggregators(),
      staleTime: 5 * 60 * 1000,
      select: (data) => ({
        providers: data,
        isAvailable: isWixPaymentProviderSupported(data),
      }),
    },
  );
}

export function useSiteUrls() {
  const metaSiteId = useMsId();

  return useQuery<GetMetaSiteUrlsResponse, Error, string>({
    ...metaSiteUrlsQueryOptions(metaSiteId),
    select: (siteUrls: GetMetaSiteUrlsResponse) => siteUrls?.metaSiteUrls?.primary?.url || '',
  });
}

export function usePrimarySiteUrlInfo() {
  const metaSiteId = useMsId();

  return useQuery<GetMetaSiteUrlsResponse, Error, PrimaryMetaSiteUrlInfo>({
    ...metaSiteUrlsQueryOptions(metaSiteId),
    select: (siteUrls: GetMetaSiteUrlsResponse) => ({
      url: siteUrls?.metaSiteUrls?.primary?.url || '',
      hasPremiumDomain: hasPremiumDomainConnected(siteUrls),
    }),
  });
}

export function useAcceptPaymentsSetup() {
  const metaSiteId = useMsId();

  return useQuery<AcceptPaymentsSetupStatus, Error, AcceptPaymentsSetupStatus>({
    queryKey: apiKeys.queries.getAcceptPaymentsSetup(metaSiteId),
    queryFn: () => getAcceptPaymentsSetupStatus(metaSiteId!),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    enabled: !!metaSiteId,
  });
}
